import { 
  ApiResponse, 
  JobStatus, 
  ProcessingResult, 
  UploadProgress,
  DetectionConfig,
  HistoryResponse,
  HistoryDetail,
  HistoryStats,
  VehicleTypesResponse
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

class ApiService {
  private baseUrl: string;
  private abortController: AbortController | null = null;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Upload video for processing
   */
  async uploadVideo(
    file: File,
    config?: Partial<DetectionConfig>,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<ApiResponse<{ job_id: string }>> {
    this.abortController = new AbortController();

    const formData = new FormData();
    formData.append('video', file);
    
    if (config) {
      formData.append('config', JSON.stringify(config));
    }

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.open('POST', `${this.baseUrl}/process_video`);
      
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          onProgress({
            loaded: event.loaded,
            total: event.total,
            percentage: Math.round((event.loaded / event.total) * 100)
          });
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve({
              success: true,
              data: response
            });
          } catch {
            reject({ success: false, error: 'Invalid response format' });
          }
        } else {
          try {
            const error = JSON.parse(xhr.responseText);
            reject({ success: false, error: error.detail || 'Upload failed' });
          } catch {
            reject({ success: false, error: `Upload failed with status ${xhr.status}` });
          }
        }
      };

      xhr.onerror = () => {
        reject({ success: false, error: 'Network error occurred' });
      };

      xhr.onabort = () => {
        reject({ success: false, error: 'Upload cancelled' });
      };

      xhr.send(formData);
    });
  }

  /**
   * Get processing status
   */
  async getProcessingStatus(jobId: string): Promise<ApiResponse<JobStatus>> {
    try {
      const response = await fetch(`${this.baseUrl}/processing_status/${jobId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        return {
          success: false,
          error: error.detail || `Failed to get status: ${response.status}`
        };
      }

      const data = await response.json();
      return {
        success: true,
        data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get processing result
   */
  async getResult(jobId: string): Promise<ApiResponse<ProcessingResult>> {
    try {
      const response = await fetch(`${this.baseUrl}/result/${jobId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        return {
          success: false,
          error: error.detail || `Failed to get result: ${response.status}`
        };
      }

      const data = await response.json();
      return {
        success: true,
        data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<ApiResponse<{ status: string }>> {
    try {
      const response = await fetch(`${this.baseUrl}/health`, {
        method: 'GET'
      });

      if (!response.ok) {
        return { success: false, error: 'Server unhealthy' };
      }

      const data = await response.json();
      return { success: true, data };
    } catch {
      return { success: false, error: 'Server unreachable' };
    }
  }

  /**
   * Cancel current upload
   */
  cancelUpload(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  /**
   * Poll for job status
   */
  pollStatus(
    jobId: string,
    onUpdate: (status: JobStatus) => void,
    interval: number = 1000
  ): () => void {
    let isPolling = true;

    const poll = async () => {
      if (!isPolling) return;

      const response = await this.getProcessingStatus(jobId);
      
      if (response.success && response.data) {
        onUpdate(response.data);

        if (response.data.status === 'completed' || response.data.status === 'error') {
          isPolling = false;
          return;
        }
      }

      if (isPolling) {
        setTimeout(poll, interval);
      }
    };

    poll();

    return () => {
      isPolling = false;
    };
  }

  // ==================== HISTORY METHODS ====================

  /**
   * Get history list
   */
  async getHistory(limit: number = 50): Promise<ApiResponse<HistoryResponse>> {
    try {
      const response = await fetch(`${this.baseUrl}/history?limit=${limit}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        return {
          success: false,
          error: error.detail || `Failed to get history: ${response.status}`
        };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get history detail for a specific job
   */
  async getHistoryDetail(jobId: string): Promise<ApiResponse<HistoryDetail>> {
    try {
      const response = await fetch(`${this.baseUrl}/history/${jobId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        return {
          success: false,
          error: error.detail || `Failed to get history detail: ${response.status}`
        };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get global history statistics
   */
  async getHistoryStats(): Promise<ApiResponse<HistoryStats>> {
    try {
      const response = await fetch(`${this.baseUrl}/history/stats/summary`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        return {
          success: false,
          error: error.detail || `Failed to get stats: ${response.status}`
        };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Delete a history item
   */
  async deleteFromHistory(jobId: string): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await fetch(`${this.baseUrl}/history/${jobId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        return {
          success: false,
          error: error.detail || `Failed to delete: ${response.status}`
        };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get available vehicle types
   */
  async getVehicleTypes(): Promise<ApiResponse<VehicleTypesResponse>> {
    try {
      const response = await fetch(`${this.baseUrl}/vehicle_types`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        return {
          success: false,
          error: error.detail || `Failed to get vehicle types: ${response.status}`
        };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  // ==================== CLIP & RE-ANALYZE ====================

  /**
   * Clip a section of a processed video and re-analyze it
   */
  async clipAndReanalyze(
    jobId: string,
    params: { start_time: number; end_time: number; vehicle_id?: string }
  ): Promise<ApiResponse<{
    job_id: string;
    stats: { total_vehicles: number; avg_speed: number; max_speed: number };
    processed_video_url?: string;
  }>> {
    try {
      const response = await fetch(`${this.baseUrl}/clip_and_reanalyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_id: jobId,
          start_time: params.start_time,
          end_time: params.end_time,
          vehicle_id: params.vehicle_id
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        return {
          success: false,
          error: error.detail || `Failed to clip and reanalyze: ${response.status}`
        };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  // ==================== DOWNLOAD METHODS ====================

  /**
   * Get direct download URL for a processed video
   */
  getDownloadUrl(jobId: string): string {
    return `${this.baseUrl}/history/${jobId}/download`;
  }

  /**
   * Download processed video programmatically
   */
  async downloadVideo(jobId: string, filename?: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/history/${jobId}/download`);
    
    if (!response.ok) {
      throw new Error('Error al descargar el vídeo');
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `video_${jobId}_analyzed.mp4`;
    document.body.appendChild(a);
    a.click();
    
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}

export const apiService = new ApiService();
export default apiService;
